import type { CalendarPost, PostInput, PostStatus } from '../../types/publishing.js';
import { dateKey, type CalendarRow } from './calendarBuckets.js';

// Publishing calendar — multi-select + (group) drag planning (§F3, 2026-07-20).
// Pure: no DOM, no Date.now, no I/O. The page owns the selection state and the
// mutations; everything about WHICH rows move and WHERE they land lives here.

// A published/publishing/partial post is read-only — Zernio can't move it and
// the platforms can't unpublish it. Such rows are never draggable and are
// EXCLUDED from a group move rather than failing it (§F3: "excluded from the
// drag with a clear indication rather than silently failing").
const IMMOVABLE: PostStatus[] = ['published', 'publishing', 'partial'];

/**
 * Can this post be mutated at all?
 *
 * TWO independent reasons a post is read-only, and they are NOT the same thing:
 *
 *  - ORIGIN: it was published natively, outside kyck. We hold no doc for it and
 *    do not own it. This gate comes FIRST because it's categorical — no status,
 *    now or later, makes someone else's post ours to move.
 *  - STATUS: it's ours, but already published/publishing/partial, so there is
 *    nothing left to reschedule.
 *
 * Callers that explain the exclusion to a user need to tell them apart — see
 * `dragExclusionReason`. Reporting an external post as "published and stayed put"
 * would be true but misleading: it implies we could have moved it earlier.
 *
 * ⚠️ This is the SINGLE chokepoint for movability. Both the calendar rows and the
 * unscheduled tray now route through it (the tray used to hand-roll the status
 * list inline, which meant a rule change here silently wouldn't reach it).
 */
export function isPostMovable(post: CalendarPost): boolean {
  if (post.origin !== 'kyck') return false;
  return !IMMOVABLE.includes(post.status);
}

export function isRowMovable(row: CalendarRow): boolean {
  return isPostMovable(row.post);
}

/**
 * Why a row can't move — so the UI can say the right thing rather than a generic
 * "read-only". null when the row CAN move.
 */
export function dragExclusionReason(row: CalendarRow): 'external' | 'published' | null {
  if (row.post.origin !== 'kyck') return 'external';
  if (IMMOVABLE.includes(row.post.status)) return 'published';
  return null;
}

/**
 * Can this row take part in a multi-select at all?
 *
 * Selection exists ONLY to stage a group drag, so anything unmovable is also
 * unselectable. Gating here rather than in the component means an external row
 * cannot enter the selection set by ANY path — there is no component-level
 * oversight that could let one slip in and then be dragged along with the group.
 */
export function isRowSelectable(row: CalendarRow): boolean {
  return isRowMovable(row);
}

// ---------------------------------------------------------------------------
// selection
// ---------------------------------------------------------------------------

export type RowSelection = ReadonlySet<string>; // of rowKey

export interface SelectionClick {
  /** Ctrl (Windows) or Cmd (macOS) — toggle this row in/out of the selection. */
  toggle: boolean;
  /** Shift — extend from the last anchor to this row. */
  range: boolean;
}

export interface SelectionState {
  selected: RowSelection;
  /** The last individually-clicked row — where a Shift range extends FROM. */
  anchor: string | null;
}

export const EMPTY_SELECTION: SelectionState = { selected: new Set<string>(), anchor: null };

/**
 * Apply a click to the selection.
 *
 * `order` is the flattened row order the user sees (all cells, reading order),
 * which is what a Shift range has to follow — a range across two day cells is
 * meaningful, a range in some internal order is not.
 *
 * A PLAIN click (no modifier) selects nothing and returns `openPost: true` —
 * clicking a row still opens it, exactly as before. Selection is strictly
 * modifier-driven, so this adds no mode and takes no behaviour away.
 *
 * `isSelectable` gates which rows may ENTER the selection (external posts may
 * not — see isRowSelectable). It defaults to allow-all so existing callers and
 * tests are unaffected. A Shift range spanning an external post selects the
 * selectable rows either side and skips it, rather than refusing the range: the
 * user's intent ("everything between these two") is honoured as far as it legally
 * can be, which beats silently doing nothing.
 */
export function applySelectionClick(
  state: SelectionState,
  rowKey: string,
  order: string[],
  click: SelectionClick,
  isSelectable: (rowKey: string) => boolean = () => true,
): { state: SelectionState; openPost: boolean } {
  if (click.range && state.anchor) {
    const from = order.indexOf(state.anchor);
    const to = order.indexOf(rowKey);
    if (from !== -1 && to !== -1) {
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      const next = new Set(state.selected);
      for (const key of order.slice(lo, hi + 1)) {
        if (isSelectable(key)) next.add(key);
      }
      // The anchor stays put so a second Shift+click re-ranges from the same
      // origin rather than walking.
      return { state: { selected: next, anchor: state.anchor }, openPost: false };
    }
  }
  if (click.toggle) {
    // An unselectable row is inert under Ctrl/Cmd — it never joins the set, and
    // it does NOT fall through to opening the post either (the user asked to
    // select, not to open).
    if (!isSelectable(rowKey)) return { state, openPost: false };
    const next = new Set(state.selected);
    if (next.has(rowKey)) next.delete(rowKey);
    else next.add(rowKey);
    return { state: { selected: next, anchor: rowKey }, openPost: false };
  }
  return { state: EMPTY_SELECTION, openPost: true };
}

/**
 * The rows a drag starting on `rowKey` actually moves.
 *
 * Dragging a SELECTED row moves the whole selection; dragging an UNSELECTED one
 * moves just that row and leaves the selection alone. (Predictable: you can
 * never accidentally move a selection you'd forgotten about by grabbing an
 * unrelated row.) Immovable rows are dropped from the result — see
 * `excludedFromDrag` for what to tell the user.
 */
export function rowsForDrag(rows: CalendarRow[], selected: RowSelection, rowKey: string): CalendarRow[] {
  const dragged = rows.find((r) => r.rowKey === rowKey);
  if (!dragged) return [];
  if (!selected.has(rowKey)) return isRowMovable(dragged) ? [dragged] : [];
  return rows.filter((r) => selected.has(r.rowKey) && isRowMovable(r));
}

/**
 * Selected rows that CANNOT move — surfaced, never silently dropped.
 *
 * In practice external rows can't be selected in the first place, so this is
 * normally the published ones. It still checks movability rather than status, so
 * that if an external row ever reaches a selection by some future path, it is
 * excluded from the move rather than trusted.
 */
export function excludedFromDrag(rows: CalendarRow[], selected: RowSelection): CalendarRow[] {
  return rows.filter((r) => selected.has(r.rowKey) && !isRowMovable(r));
}

// ---------------------------------------------------------------------------
// where the rows land
// ---------------------------------------------------------------------------

export interface RowMove {
  rowKey: string;
  postId: string;
  targetIndex: number;
  /** The new effective time for this destination, ISO. */
  scheduledFor: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole calendar days between two local day keys (b - a). */
export function dayDelta(fromDayKey: string, toDayKey: string): number {
  const parse = (k: string) => {
    const [y, m, d] = k.split('-').map(Number);
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getTime();
  };
  return Math.round((parse(toDayKey) - parse(fromDayKey)) / DAY_MS);
}

/**
 * Plan a (possibly group) move onto `targetDayKey`.
 *
 * THE RULE (§F3): move the DATE, preserve each row's TIME OF DAY and the
 * relative day offsets between rows. Dropping five rows spanning Mon 9am →
 * Fri 5pm onto next Tuesday keeps that whole shape, shifted — it does not
 * collapse them onto one day or one time. The shift is computed from the
 * ANCHOR row (the one actually grabbed), so the row under the cursor lands
 * exactly where it was dropped and everything else moves with it.
 *
 * A month cell is day-granular, so the drop implies a DAY, never a time —
 * which is precisely why times are preserved rather than derived from the drop.
 * (A future week/time-slot view would imply a time, and would need its own
 * rule; flagged in the brief.)
 *
 * Rows with no current date (an undated draft dragged in from the tray) take
 * the anchor's time of day, defaulting to 9am — there is no time to preserve.
 */
export function planRowMoves(
  rows: CalendarRow[],
  anchorRowKey: string,
  targetDayKey: string,
): RowMove[] {
  if (rows.length === 0) return [];
  const anchor = rows.find((r) => r.rowKey === anchorRowKey) ?? rows[0]!;
  const anchorDate = anchor.scheduledFor ? new Date(anchor.scheduledFor) : null;
  const anchorValid = anchorDate && !Number.isNaN(anchorDate.getTime());
  const delta = anchorValid ? dayDelta(dateKey(anchorDate!), targetDayKey) : null;

  return rows.map((row) => {
    const own = row.scheduledFor ? new Date(row.scheduledFor) : null;
    const ownValid = own && !Number.isNaN(own.getTime());

    let next: Date;
    if (ownValid && delta !== null) {
      // Shift by whole days, preserving this row's own time of day.
      next = new Date(own!);
      next.setDate(next.getDate() + delta);
    } else {
      // No date to shift from — land on the drop day, at the anchor's time of
      // day (or 9am).
      const [y, m, d] = targetDayKey.split('-').map(Number);
      const hours = anchorValid ? anchorDate!.getHours() : 9;
      const minutes = anchorValid ? anchorDate!.getMinutes() : 0;
      next = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, hours, minutes, 0, 0);
    }
    return {
      rowKey: row.rowKey,
      postId: row.postId,
      targetIndex: row.targetIndex,
      scheduledFor: next.toISOString(),
    };
  });
}

/** Group a move plan by post — one save per post, however many of its rows moved. */
export function groupMovesByPost(moves: RowMove[]): Map<string, RowMove[]> {
  const out = new Map<string, RowMove[]>();
  for (const m of moves) {
    const list = out.get(m.postId);
    if (list) list.push(m);
    else out.set(m.postId, [m]);
  }
  return out;
}

/**
 * Fold a post's moves into its PostInput.
 *
 * TWO CASES, and the distinction matters for how the data reads afterwards:
 *
 *  1. EVERY destination moved and none had its own time — i.e. the whole post
 *     is on one schedule and is being moved wholesale (the overwhelmingly
 *     common case, and the only one that existed before this batch). Then the
 *     POST-LEVEL time moves and the targets keep inheriting. No per-target
 *     overrides are invented, so a simple post stays simple.
 *
 *  2. Anything else — a single destination moved, or some already had their own
 *     time. Then only the moved targets get an explicit `scheduledFor`, and the
 *     post-level time is left alone so the destinations that did NOT move stay
 *     exactly where they are.
 *
 * Always switches to `schedule` mode: dropping something on a day is the act of
 * scheduling it, including for a draft dragged out of the tray.
 */
export function applyMovesToInput(input: PostInput, moves: RowMove[]): PostInput {
  const byIndex = new Map(moves.map((m) => [m.targetIndex, m.scheduledFor]));
  const everyTargetMoved =
    input.targets.length > 0 && input.targets.every((_, i) => byIndex.has(i));
  const noneHadOwnTime = input.targets.every((t) => !t.scheduledFor);

  if (everyTargetMoved && noneHadOwnTime) {
    return {
      ...input,
      publishMode: 'schedule',
      scheduledFor: byIndex.get(0) ?? input.scheduledFor,
      targets: input.targets.map((t) => ({ ...t, scheduledFor: null })),
    };
  }

  return {
    ...input,
    publishMode: 'schedule',
    // A scheduled post always needs a post-level anchor: the destinations that
    // did NOT move inherit it, and Zernio treats a post with no top-level time
    // as a draft. Only supplied when there wasn't one (so a real move never
    // shifts the destinations it wasn't asked to touch).
    scheduledFor: input.scheduledFor ?? earliest(moves),
    targets: input.targets.map((t, i) => {
      const moved = byIndex.get(i);
      return moved ? { ...t, scheduledFor: moved } : t;
    }),
  };
}

function earliest(moves: RowMove[]): string | null {
  if (moves.length === 0) return null;
  return moves.reduce((a, b) => (a.scheduledFor <= b.scheduledFor ? a : b)).scheduledFor;
}
